import React, { useState, useMemo } from "react";
import { usePurchases } from "../hooks/usePurchases.js";
import { PurchaseCreateModal } from "../components/PurchaseCreateModal.jsx";
import { getPurchaseById } from "../services/purchaseService.js";
import { PurchaseDetailModal } from "../components/PurchaseDetailModal.jsx";
import { VendorPaymentModal } from "../components/VendorPaymentModal.jsx";
import { PaymentHistoryModal } from "../../invoices/components/PaymentHistoryModal.jsx";
import { PaymentReceiptModal } from "../../invoices/components/PaymentReceiptModal.jsx";
import { GstReportModal } from "../../reports/components/GstReportModal.jsx";
import { Card } from "../../../shared/components/Card.jsx";
import { Badge } from "../../../shared/components/Badge.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { useCompanies } from "../../companies/hooks/useCompanies.js";
import { formatAppDate } from "../../../shared/utils/dateUtils.js";
import { useActiveCompany } from "../../../shared/context/ActiveCompanyContext.jsx";
import { OtherCompanyPrompt } from "../../../shared/components/OtherCompanyPrompt.jsx";
import { UNASSIGNED_COMPANY, getRecordCompanyKey } from "../../../shared/utils/companyScope.js";
import { useIncrementalList } from "../../../shared/hooks/useIncrementalList.js";
import { ListSentinel } from "../../../shared/components/ListSentinel.jsx";
import { useRoutedSelection } from "../../../shared/hooks/useRoutedSelection.js";
import { useListView } from "../../../shared/hooks/useListView.js";
import { DataTable, sortRows, readStoredSort } from "../../../shared/components/DataTable.jsx";
import { SplitPane, RecordList, RecordNav, useSplitScroll } from "../../../shared/components/SplitView.jsx";
import { paymentStatusVariant } from "../../../shared/components/tableColumns.jsx";
import { useSetPageHeading } from "../../../shared/context/PageHeadingContext.jsx";
import { ViewToggle, StickyFooter } from "../../../shared/components/ListChrome.jsx";
import { purchaseColumns } from "../components/purchaseColumns.jsx";

export function PurchasesPage() {
  const { purchases, loading, error, addPurchase, recordPayment, removePurchase, refetch: refetchPurchases } = usePurchases();
  const { companies, defaultCompany } = useCompanies();

  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("ALL"); // 'ALL' | 'UNPAID' | 'PAID' | 'GST'
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  // Edit starts from a fresh copy, so a payment recorded a moment ago is included
  const openEditor = async (bill) => {
    try {
      setEditingPurchase((await getPurchaseById(bill.id)) || bill);
    } catch (_) {
      setEditingPurchase(bill);
    }
  };
  // Open bill lives in the URL (/purchases/:purchaseId) so Back closes it and links can be shared
  const purchaseRoute = useRoutedSelection({ items: purchases, loading, paramName: "purchaseId", basePath: "/purchases" });
  const { activeCompany, activeCompanyId, setActiveCompany, ownsRecord, companyOfRecord } = useActiveCompany();
  // Only the header company's bills open; another company's bill shows a "switch company?" prompt instead
  const routedSelectedPurchase = purchaseRoute.selected;
  const otherCompanyBill = routedSelectedPurchase && !ownsRecord(routedSelectedPurchase) ? routedSelectedPurchase : null;
  const selectedPurchase = otherCompanyBill ? null : routedSelectedPurchase;
  const [isVendorPayOpen, setIsVendorPayOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isGstOpen, setIsGstOpen] = useState(false);
  const [selectedReceiptPayment, setSelectedReceiptPayment] = useState(null);

  // Working company (picked in the header): only its bills are listed
  const companyPurchases = useMemo(() => purchases.filter(ownsRecord), [purchases, ownsRecord]);
  const isAllCompanies = false;
  const isUnassigned = (r) => getRecordCompanyKey(r, companies) === UNASSIGNED_COMPANY;
  const listView = useListView("purchases", { onNew: () => setIsCreateOpen(true) });
  const columns = useMemo(
    () => purchaseColumns({ defaultCompany, showCompany: isAllCompanies && companies.length > 1 }),
    [defaultCompany, isAllCompanies, companies.length]
  );

  // Aggregated KPIs for the selected company
  const totalPurchases = useMemo(() => {
    return companyPurchases.reduce((sum, p) => sum + Number(p.grandTotal || 0), 0);
  }, [companyPurchases]);

  const totalPayable = useMemo(() => {
    return companyPurchases.reduce((sum, p) => sum + Number(p.balance || 0), 0);
  }, [companyPurchases]);

  // Filter & Search
  const filteredPurchases = useMemo(() => {
    return companyPurchases.filter((p) => {
      const term = searchTerm.toLowerCase().trim();
      const billNo = (p.vendorBillNo || "").toLowerCase();
      const vendor = (p.vendorName || "").toLowerCase();

      const matchesSearch = !term || billNo.includes(term) || vendor.includes(term);
      if (!matchesSearch) return false;

      if (activeFilter === "GST") {
        return p.billType !== "Normal" && !p.isNonGst;
      }
      if (activeFilter === "NORMAL") {
        return p.billType === "Normal" || p.isNonGst;
      }
      if (activeFilter === "UNPAID") {
        return p.paymentStatus === "Unpaid" || p.paymentStatus === "Partial";
      }
      if (activeFilter === "PAID") {
        return p.paymentStatus === "Paid";
      }

      return true;
    });
  }, [companyPurchases, searchTerm, activeFilter]);

  // Desktop: an open bill shows beside the list (in the table's sort order) instead of in a dialog
  const showSplit = listView.isDesktop && !!selectedPurchase;
  const orderedPurchases = useMemo(
    () => (showSplit ? sortRows(filteredPurchases, columns, readStoredSort("purchases")) : filteredPurchases),
    [showSplit, filteredPurchases, columns]
  );
  const selectedIndex = showSplit ? orderedPurchases.findIndex((p) => p.id === selectedPurchase.id) : -1;
  useSplitScroll(showSplit);
  useSetPageHeading(showSplit ? `#${selectedPurchase.vendorBillNo || "No Bill #"}` : null, purchaseRoute.close);

  // Render the filtered list in chunks of 30, appending as the user scrolls
  const purchaseList = useIncrementalList(filteredPurchases, {
    resetKey: `${activeCompanyId}|${activeFilter}|${searchTerm}`
  });

  const handlePurchaseCreated = async (payload) => {
    await addPurchase(payload);
  };

  const handleRecordPayment = async (purchaseId, paymentData) => {
    await recordPayment(purchaseId, paymentData);
  };

  return (
    <div className="purchases-page">
      {showSplit ? (
        <SplitPane
          storageKey="purchases"
          left={(
            <RecordList
              rows={orderedPurchases}
              selectedId={selectedPurchase.id}
              onSelect={(p) => purchaseRoute.step(p.id)}
              renderRow={(p) => ({
                title: p.vendorBillNo || "No Bill #",
                subtitle: `${p.vendorName} • ${formatAppDate(p.billDate, p.companyDetails?.dateFormat || defaultCompany?.dateFormat)}`,
                amount: `₹${Number(p.grandTotal || 0).toLocaleString("en-IN")}`,
                badge: <Badge variant={paymentStatusVariant(p.paymentStatus)}>{p.paymentStatus || "Unpaid"}</Badge>
              })}
              searchValue={searchTerm}
              onSearch={setSearchTerm}
              searchPlaceholder="Search by vendor bill # or vendor name..."
              noun="bills"
              resetKey={`${activeCompanyId}|${activeFilter}|${searchTerm}`}
            />
          )}
          right={(
            <>
              <RecordNav
                index={selectedIndex}
                total={orderedPurchases.length}
                onPrev={() => purchaseRoute.step(orderedPurchases[selectedIndex - 1].id)}
                onNext={() => purchaseRoute.step(orderedPurchases[selectedIndex + 1].id)}
                onClose={purchaseRoute.close}
              />
              <PurchaseDetailModal
                key={selectedPurchase.id}
                variant="panel"
                isOpen
                onClose={purchaseRoute.close}
                purchase={selectedPurchase}
                onRecordPayment={handleRecordPayment}
                onDeletePurchase={removePurchase}
                onEditPurchase={openEditor}
                onPaymentsChanged={refetchPurchases}
              />
            </>
          )}
        />
      ) : (
        <>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", gap: "0.5rem", width: "100%" }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2 style={{ fontSize: "1.25rem", fontWeight: "700" }}>Vendor Purchase Bills</h2>
            <p style={{ fontSize: "0.8rem", color: "var(--text-dim)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {filteredPurchases.length} {filteredPurchases.length === 1 ? "bill" : "bills"} inwarded
            </p>
          </div>
          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", alignItems: "center", flexShrink: 0 }}>
            <Button size="sm" variant="secondary" onClick={() => setIsGstOpen(true)} style={{ fontSize: "0.78rem" }}>
              🏛️ GST Report
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setIsHistoryOpen(true)} style={{ fontSize: "0.78rem" }}>
              📜 Payment History
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setIsVendorPayOpen(true)} style={{ fontSize: "0.78rem" }}>
              💸 Pay Vendor
            </Button>
            <Button size="sm" variant="primary" onClick={() => setIsCreateOpen(true)} style={{ flexShrink: 0 }}>
              New Bill
            </Button>
          </div>
        </div>

        {/* KPI Metrics Row */}
        <OtherCompanyPrompt
          isOpen={!!otherCompanyBill}
          kind="purchase bill"
          billNo={otherCompanyBill?.vendorBillNo || ""}
          company={otherCompanyBill ? companyOfRecord(otherCompanyBill) : null}
          onSwitch={() => setActiveCompany(companyOfRecord(otherCompanyBill).id)}
          onCancel={purchaseRoute.close}
        />
        {purchaseRoute.notFound && (
          <div style={{ background: "rgba(245, 158, 11, 0.12)", border: "1px solid rgba(245, 158, 11, 0.35)", color: "var(--warning)", padding: "0.55rem 0.85rem", borderRadius: "var(--radius-md)", fontSize: "0.82rem", marginBottom: "0.75rem" }}>
            ⚠️ That purchase bill could not be found. It may have been deleted.
          </div>
        )}

        <div className="metrics-row">
          <div className="metric-card">
            <div className="metric-label">Total Purchased{activeCompany ? ` · ${activeCompany.name}` : ""}</div>
            <div className="metric-value">₹{totalPurchases.toLocaleString("en-IN")}</div>
          </div>
          <div className="metric-card" style={{ borderLeft: totalPayable > 0 ? "3px solid var(--danger)" : "1px solid var(--border-subtle)" }}>
            <div className="metric-label">To Pay (Vendors){activeCompany ? ` · ${activeCompany.name}` : ""}</div>
            <div className="metric-value" style={{ color: totalPayable > 0 ? "var(--danger)" : "var(--success)" }}>
              ₹{totalPayable.toLocaleString("en-IN")}
            </div>
          </div>
        </div>

        {/* Filter pills + search (one sticky toolbar row on desktop) */}
        <div className="list-toolbar">
          <div className="filter-pills-row">
            <button
              type="button"
              className={`filter-pill ${activeFilter === "ALL" ? "active" : ""}`}
              onClick={() => setActiveFilter("ALL")}
            >
              All ({companyPurchases.length})
            </button>
            <button
              type="button"
              className={`filter-pill ${activeFilter === "UNPAID" ? "active" : ""}`}
              onClick={() => setActiveFilter("UNPAID")}
            >
              Unpaid / Due
            </button>
            <button
              type="button"
              className={`filter-pill ${activeFilter === "PAID" ? "active" : ""}`}
              onClick={() => setActiveFilter("PAID")}
            >
              Paid
            </button>
            <button
              type="button"
              className={`filter-pill ${activeFilter === "GST" ? "active" : ""}`}
              onClick={() => setActiveFilter("GST")}
            >
              GST Bills
            </button>
          </div>

          {/* Search Input */}
          <div className="search-container">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              className="search-input"
              placeholder="Search by vendor bill # or vendor name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <ViewToggle view={listView.view} onChange={listView.setView} />
        </div>

        {loading && (
          <div className="state-box">
            <div className="state-icon">⏳</div>
            <div className="state-title">Loading purchase bills...</div>
          </div>
        )}

        {error && (
          <div className="state-box">
            <div className="state-icon">⚠️</div>
            <div className="state-title">Error Loading Bills</div>
            <div className="state-desc">{error}</div>
          </div>
        )}

        {!loading && !error && filteredPurchases.length === 0 && (
          <div className="state-box">
            <div className="state-icon">🛒</div>
            <div className="state-title">No purchase bills found</div>
            <div className="state-desc">
              {searchTerm ? "No bills match your search criteria." : "Record your first vendor bill to inward stock automatically."}
            </div>
            <Button variant="primary" onClick={() => setIsCreateOpen(true)}>
              + Record Purchase Bill
            </Button>
          </div>
        )}

        {listView.showTable ? (
          <DataTable
            columns={columns}
            rows={filteredPurchases}
            onRowClick={(p) => purchaseRoute.open(p.id)}
            selectedId={selectedPurchase?.id}
            storageKey="purchases"
            noun="bills"
            resetKey={`${activeCompanyId}|${activeFilter}|${searchTerm}`}
          />
        ) : (
          <>
            {/* Purchases List */}
            <div className="purchases-list card-grid">
              {purchaseList.visibleItems.map((p) => {
                const statusVariant = p.paymentStatus === "Paid" ? "success" : (p.paymentStatus === "Partial" ? "warning" : "danger");
                const isNonGst = p.billType === "Normal" || p.isNonGst;

                return (
                  <Card
                    key={p.id}
                    onClick={() => purchaseRoute.open(p.id)}
                    style={{ cursor: "pointer", marginBottom: "0.75rem" }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
                          <span style={{ fontWeight: "700", fontSize: "1rem", color: "var(--text-main)" }}>
                            {p.vendorBillNo || "No Bill #"}
                          </span>
                          {isNonGst ? (
                            <span style={{ fontSize: "0.7rem", padding: "1px 6px", borderRadius: "4px", background: "rgba(245, 158, 11, 0.15)", color: "#fbbf24", fontWeight: "600", border: "1px solid rgba(245, 158, 11, 0.3)" }}>
                              Non-GST
                            </span>
                          ) : (
                            <span style={{ fontSize: "0.7rem", padding: "1px 6px", borderRadius: "4px", background: "rgba(99, 102, 241, 0.18)", color: "#818cf8", fontWeight: "600", border: "1px solid rgba(99, 102, 241, 0.4)" }}>
                              GST
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginTop: "2px" }}>
                          🏢 {p.vendorName} • {(p.lineItems || []).length} item{(p.lineItems || []).length === 1 ? "" : "s"} inwarded
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: "4px" }}>
                          📅 {formatAppDate(p.billDate, p.companyDetails?.dateFormat || defaultCompany?.dateFormat)}
                        </div>
                        {isUnassigned(p) && (
                          <div style={{ fontSize: "0.68rem", color: "var(--warning, #b45309)", marginTop: "2px", fontWeight: 600 }}>No company</div>
                        )}
                        {isAllCompanies && companies.length > 1 && p.companyName && (
                          <div style={{ fontSize: "0.72rem", color: "var(--text-dim)", marginTop: "2px" }}>
                            🏬 For {p.companyName}
                          </div>
                        )}
                      </div>

                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: "1.15rem", fontWeight: "800", color: "#818cf8", marginBottom: "4px" }}>
                          ₹{Number(p.grandTotal || 0).toLocaleString("en-IN")}
                        </div>
                        <Badge variant={statusVariant}>
                          {p.paymentStatus || "Unpaid"}
                        </Badge>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
            <ListSentinel
              sentinelRef={purchaseList.sentinelRef}
              hasMore={purchaseList.hasMore}
              visibleCount={purchaseList.visibleCount}
              total={purchaseList.total}
              onShowMore={purchaseList.showMore}
            />
          </>
        )}

        <StickyFooter
          shown={filteredPurchases.length}
          total={companyPurchases.length}
          noun="bills"
          chips={[
            { label: "Purchased", value: `₹${totalPurchases.toLocaleString("en-IN")}` },
            { label: "To pay", value: `₹${totalPayable.toLocaleString("en-IN")}`, tone: totalPayable > 0 ? "danger" : "success" }
          ]}
        />
        </>
      )}

      {/* Floating Action Button */}
      <button
        type="button"
        className="fab"
        onClick={() => setIsCreateOpen(true)}
        aria-label="New Purchase Bill"
      >
        +
      </button>

      {/* Purchase Create Modal */}
      <PurchaseCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onPurchaseCreated={handlePurchaseCreated}
        initialCompanyId={activeCompanyId}
      />

      {/* Edit a saved purchase bill: the same steps, filled in */}
      <PurchaseCreateModal
        isOpen={!!editingPurchase}
        onClose={() => setEditingPurchase(null)}
        editingPurchase={editingPurchase}
        onPurchaseUpdated={refetchPurchases}
      />

      {/* Purchase Detail Modal */}
      <PurchaseDetailModal
        isOpen={!showSplit && !!selectedPurchase}
        onClose={purchaseRoute.close}
        purchase={selectedPurchase}
        onRecordPayment={handleRecordPayment}
        onDeletePurchase={removePurchase}
        onEditPurchase={openEditor}
        onPaymentsChanged={refetchPurchases}
      />

      {/* Vendor Multi-Bill Payment Modal */}
      <VendorPaymentModal
        isOpen={isVendorPayOpen}
        onClose={() => setIsVendorPayOpen(false)}
      />

      {/* Payment History Modal */}
      <PaymentHistoryModal
        isOpen={isHistoryOpen}
        onPaymentsChanged={refetchPurchases}
        onClose={() => setIsHistoryOpen(false)}
        filterType="PAYMENT_OUT"
        onViewReceipt={(payment) => {
          setIsHistoryOpen(false);
          setSelectedReceiptPayment(payment);
        }}
      />

      {/* Payment Voucher Viewer */}
      <PaymentReceiptModal
        isOpen={!!selectedReceiptPayment}
        onClose={() => setSelectedReceiptPayment(null)}
        payment={selectedReceiptPayment}
      />

      {/* GST Filing Report & Export Modal */}
      <GstReportModal
        isOpen={isGstOpen}
        onClose={() => setIsGstOpen(false)}
      />
    </div>
  );
}

export default PurchasesPage;
